/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  InputAdornment,
  Button,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { icons } from "../../../svg";
import {
  FreightRail,
  FreightTruck,
  IDestinationExtension,
  IQuoteExtension,
  IQuoteExtensionOld,
} from "../../../types/ICompAnalysisQMD";
import RailIconFO from "../../../icons/RailIconFO";
import TruckIconFO from "../../../icons/TruckIconFO";
import { WarningTriangleIcon } from "../../../icons/WarningTriangle";
import { QuotedRateIcon } from "../../../icons/QuotedRateIcon";
import {
  calculateFobDifference,
  updateInboundModes,
  buildMissingTruckOriginSections,
  buildMissingRailOriginSections,
  isRailFreightQuotedOrNotified,
  parseFobSubTextRange,
  getQuoteRowKey,
  sortQuotesByNetbackAndDlvd,
} from "./multiLocationQuoteUtils";
import {
  TruckMissingOriginsTable,
  buildRows,
  TableRow as TruckMissingTableRow,
} from "./TruckMissingOriginsTable";
import {
  RailMissingOriginsTable,
  buildRows as buildRailRows,
  TableRow as RailMissingTableRow,
} from "./RailMissingOriginsTable";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";

import { useNitroStore } from "../../../store/Store";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useStickyTableHeader } from "../../../hooks/useStickyTableHeader";
import { QuotedRateNotifiedIcon } from "../../../icons/QuotedRateNotifiedIcon";
import { toCarrierIds } from "../../../utils/carrierUtils";
import { toModes } from "../../../utils/utils";
import { PricePreference } from "./KochDlvdOfferToggle";
import { getOriginRowPricing } from "./pricingDisplay";
import { MissingFreightTable } from "./MissingFreightTable";

// TODO: if user types something in what do we do with optimal origin?
// TODO: do we need to reset optimals?
// TODO: kf-enid plant has 105.4 freight?
// TODO: netback styles change based on dlvd cell width
// TODO: implement mode filters
// TODO: why is review freight at bottom?
// TODO: ADM Alter Facility is showing rail when it is not toggled
// TODO: favorite origin grey is not reaching the bottom
// TODO: adding Growmark Cincinnati as fav origin causes review freight button to look weird
// TODO: do we need to add auto scroll for primary destinations?
// TODO: reset button on dlvd offer causes inbound mode spacing to be off
// TODO: should we add timeouts for other reload actions like adding/removing inbound modes to allow smoother animations?
interface OriginTableProps {
  quotes: IQuoteExtension[];
  destination: IDestinationExtension;
  resetTrigger?: number;
  kochDlvdOfferMode?: PricePreference;
}

// single source of truth for the row/global override priority: row netback > row delivered offer > global delivered offer
const computeNetbackOverride = (
  freight: number,
  rowSpecificNetback?: string,
  rowSpecificDeliveredOffer?: string,
  globalDeliveredOfferInput?: string,
): number | null => {
  if (rowSpecificNetback) return parseFloat(rowSpecificNetback);
  if (rowSpecificDeliveredOffer)
    return parseFloat(rowSpecificDeliveredOffer) - freight;
  if (globalDeliveredOfferInput && globalDeliveredOfferInput.trim() !== "")
    return parseFloat(globalDeliveredOfferInput) - freight;
  return null;
};

const formatNetback = (value: number): string =>
  Number.isFinite(value) ? `$${Math.round(value)}` : "--";

export const OriginTable = forwardRef<HTMLElement, OriginTableProps>(
  ({ quotes, destination, resetTrigger, kochDlvdOfferMode }, ref) => {
    const kochPricePreference: PricePreference = kochDlvdOfferMode || {
      truck: "px",
      rail: "alt",
    };
    const isMobile = useIsMobile();
    // INITIAL SETUP
    const [
      setDisplayFreightDetailsPanel,
      setRailFreightDetailContext,
      setDisplayTruckFreightDetailsPanel,
      setSelectedTruckDetailRecord,
      setAnchorForTruckDetailsPanel,
      setDisplayAdditionalCarriersPanel,
      setAdditionalCarriers,
      setAnchorForAdditionalCarriersPanel,
      setDisplayInboundModeTogglePanel,
      setAnchorForInboundModesTogglePanel,
      setInboundModes,
      setDestinationToTroubleshoot,
      setDisplayAddInboundRailPanel,
      expandRailOriginsWithNoFreightTrigger,
      expandTruckOriginsWithNoFreightTrigger,
      setExpandRailOriginsWithNoFreightTrigger,
      resetExpandRailOriginsWithNoFreight,
      setExpandTruckOriginsWithNoFreightTrigger,
      resetExpandTruckOriginsWithNoFreight,
      setExpandedDestination,
      railTeamNotifiedRows,
      registerMoveToOriginTable,
      unregisterMoveToOriginTable,
    ] = useNitroCAStore((state) => [
      state.setDisplayRailFreightDetailsPanel,
      state.setRailFreightDetailContext,
      state.setDisplayTruckFreightDetailsPanel,
      state.setSelectedTruckDetailRecord,
      state.setAnchorForTruckDetailsPanel,
      state.setDisplayAdditionalCarriersPanel,
      state.setAdditionalCarriers,
      state.setAnchorForAdditionalCarriersPanel,
      state.setDisplayInboundModeTogglePanel,
      state.setAnchorForInboundModesTogglePanel,
      state.setInboundModes,
      state.setDestinationToTroubleshoot,
      state.setDisplayAddInboundRailPanel,
      state.expandRailOriginsWithNoFreightTrigger,
      state.expandTruckOriginsWithNoFreightTrigger,
      state.setExpandRailOriginsWithNoFreightTrigger,
      state.resetExpandRailOriginsWithNoFreight,
      state.setExpandTruckOriginsWithNoFreightTrigger,
      state.resetExpandTruckOriginsWithNoFreight,
      state.setExpandedDestination,
      state.railTeamNotifiedRows,
      state.registerMoveToOriginTable,
      state.unregisterMoveToOriginTable,
    ]);
    const [
      setMultiLocationDestinations,
      product,
      currentFilters,
      featureFlags,
    ] = useNitroStore((state) => [
      state.setMultiLocationDestinations,
      state.product,
      state.currentFilters,
      state.featureFlags,
    ]);
    const { originManager, manageRailRate } = featureFlags;
    const triggerRefetchMultiLocation = useNitroCAStore(
      (state) => state.triggerRefetchMultiLocation,
    );
    // global delivered offer input state
    const [globalDeliveredOfferInput, setGlobalDeliveredOfferInput] =
      useState<string>("");
    const [globalOfferInputChanged, setGlobalOfferInputChanged] =
      useState<boolean>(false);
    // row specific delivered offer and netback input states
    const [rowDeliveredValues, setRowDeliveredValues] = useState<
      Record<string, string>
    >({});
    const [rowNetbackValues, setRowNetbackValues] = useState<
      Record<string, string>
    >({});
    // stores original values for each row when user starts editing
    const [originalValues, setOriginalValues] = useState<
      Record<string, { dlvd: string; fob: string }>
    >({});
    // hovered row states for delivered offer and netback
    const [hoveredDeliveredRowKey, setHoveredDeliveredRowKey] = useState<
      string | null
    >(null);
    const [hoveredNetbackRowKey, setHoveredNetbackRowKey] = useState<
      string | null
    >(null);
    // editing row states for delivered offer and netback
    const [editingDeliveredRowKey, setEditingDeliveredRowKey] = useState<
      string | null
    >(null);
    const [editingNetbackRowKey, setEditingNetbackRowKey] = useState<
      string | null
    >(null);
    const replaceNetbackOnNextDigit = useRef<string | null>(null);
    const { ref: paperRef, isSticky: isHeaderSticky } = useStickyTableHeader(
      "deskstop-destination-table",
    );
    const [showTruckQuotesWithNoFreight, setShowTruckQuotesWithNoFreight] =
      useState(false);
    const [showRailQuotesWithNoFreight, setShowRailQuotesWithNoFreight] =
      useState(false);
    const [truckMissingRows, setTruckMissingRows] = useState<
      TruckMissingTableRow[]
    >([]);
    const [truckMissingLoading, setTruckMissingLoading] = useState(false);
    const [railMissingRows, setRailMissingRows] = useState<
      RailMissingTableRow[]
    >([]);
    const [railMissingLoading, setRailMissingLoading] = useState(false);
    const [quotesWithFreight, setQuotesWithFreight] = useState<
      IQuoteExtension[]
    >([]);
    const [quotesWithNoFreight, setQuotesWithNoFreight] = useState<
      IQuoteExtension[]
    >([]);

    // koch quotes can either have no freight or only a public tariff rate
    // competitor quotes simply have freight or not
    const isMissingFreight = (quote: IQuoteExtension) => {
      return (
        !quote.freight ||
        (!quote.competitor &&
          quote.freight.totalFreight &&
          (quote.freight as FreightRail).isExternal)
      );
    };

    useEffect(() => {
      const eligibleQuotes = quotes.filter((quote) => {
        const outboundModes = quote.origin.details?.outboundModes;
        if (!outboundModes) return true;
        return outboundModes.includes(quote.mode);
      });

      const missingFreightQuotes = eligibleQuotes.filter((quote) =>
        isMissingFreight(quote),
      );

      const hasFreightQuotes = eligibleQuotes.filter(
        (quote) => !isMissingFreight(quote),
      );

      setQuotesWithFreight(hasFreightQuotes);
      setQuotesWithNoFreight(missingFreightQuotes);
    }, [isMobile, quotes, setQuotesWithFreight, setQuotesWithNoFreight]);

    // this table's state is new-schema only; old-schema rows can be registered but are never inserted
    const isNewQuote = (
      q: IQuoteExtension | IQuoteExtensionOld,
    ): q is IQuoteExtension => "postedPrice" in q && originManager;

    const moveToOriginTable = React.useCallback(
      ({
        quote,
        foundQuote,
      }: {
        quote: IQuoteExtension | IQuoteExtensionOld;
        foundQuote: IQuoteExtension | IQuoteExtensionOld;
      }) => {
        if (isNewQuote(foundQuote)) {
          setQuotesWithFreight((prev) => {
            // Insert-if-missing: this flow should only append new competitor
            // rows, but we guard against duplicate poll completions.
            if (
              prev.some((q) => getQuoteRowKey(q) === getQuoteRowKey(foundQuote))
            ) {
              return prev;
            }
            return [...prev, foundQuote];
          });
        }

        setQuotesWithNoFreight((prev) =>
          prev.filter((q) => getQuoteRowKey(q) !== getQuoteRowKey(quote)),
        );
      },
      [],
    );

    useEffect(() => {
      registerMoveToOriginTable(destination.id, moveToOriginTable);
      return () => unregisterMoveToOriginTable(destination.id);
    }, [
      destination.id,
      moveToOriginTable,
      registerMoveToOriginTable,
      unregisterMoveToOriginTable,
    ]);

    const truckQuotesWithNoFreight = quotesWithNoFreight.filter(
      (quote) => quote.mode === "TRUCK",
    );
    const railQuotesWithNoFreight = quotesWithNoFreight.filter(
      (quote) => quote.mode === "RAIL",
    );

    const quoteDeliveredOfferByRowKey = useMemo(() => {
      const deliveredByRowKey = new Map<string, number>();
      const normalizedGlobalInput = globalDeliveredOfferInput.trim();
      const globalDeliveredOffer =
        normalizedGlobalInput === ""
          ? null
          : Number(parseFloat(normalizedGlobalInput).toFixed(0));

      quotesWithFreight.forEach((quote) => {
        const rowKey = getQuoteRowKey(quote);
        const rowDeliveredOffer = rowDeliveredValues[rowKey];

        if (rowDeliveredOffer && rowDeliveredOffer.trim() !== "") {
          const parsedRowValue = Number(
            parseFloat(rowDeliveredOffer).toFixed(2),
          );
          if (Number.isFinite(parsedRowValue)) {
            deliveredByRowKey.set(rowKey, parsedRowValue);
            return;
          }
        }

        if (
          globalDeliveredOffer !== null &&
          Number.isFinite(globalDeliveredOffer)
        ) {
          deliveredByRowKey.set(rowKey, globalDeliveredOffer);
          return;
        }

        const { initialDeliveredOffer } = getOriginRowPricing(
          quote,
          kochPricePreference,
        );
        if (Number.isFinite(initialDeliveredOffer)) {
          deliveredByRowKey.set(rowKey, initialDeliveredOffer);
        }
      });

      return deliveredByRowKey;
    }, [
      kochPricePreference,
      quotesWithFreight,
      globalDeliveredOfferInput,
      originManager,
      rowDeliveredValues,
    ]);

    const optimalExcludedRowKeys = useMemo(() => {
      const excludedRowKeys = new Set<string>();

      quotesWithFreight.forEach((quote) => {
        const rowKey = getQuoteRowKey(quote);
        const rowSpecificDeliveredOffer = rowDeliveredValues[rowKey];
        const rowSpecificNetback = rowNetbackValues[rowKey];
        const { freight, initialNetbackDisplay } = getOriginRowPricing(
          quote,
          kochPricePreference,
        );

        const netbackOverride = computeNetbackOverride(
          freight,
          rowSpecificNetback,
          rowSpecificDeliveredOffer,
          globalDeliveredOfferInput,
        );
        const renderedNetbackText =
          netbackOverride !== null
            ? formatNetback(netbackOverride)
            : initialNetbackDisplay;

        if (
          renderedNetbackText === "DISC" ||
          renderedNetbackText === "N/O" ||
          renderedNetbackText === "--"
        ) {
          excludedRowKeys.add(rowKey);
        }
      });

      return excludedRowKeys;
    }, [
      kochPricePreference,
      quotesWithFreight,
      globalDeliveredOfferInput,
      originManager,
      rowDeliveredValues,
      rowNetbackValues,
    ]);

    const optimalKochRowKey = useMemo(() => {
      let bestRowKey: string | null = null;
      let bestDeliveredOffer = Number.POSITIVE_INFINITY;

      quotesWithFreight.forEach((quote) => {
        if (quote.competitor) {
          return;
        }
        const rowKey = getQuoteRowKey(quote);
        if (optimalExcludedRowKeys.has(rowKey)) {
          return;
        }
        const deliveredOffer = quoteDeliveredOfferByRowKey.get(rowKey);
        if (deliveredOffer === undefined || !Number.isFinite(deliveredOffer)) {
          return;
        }
        if (deliveredOffer < bestDeliveredOffer) {
          bestDeliveredOffer = deliveredOffer;
          bestRowKey = rowKey;
        }
      });

      return bestRowKey;
    }, [
      quotesWithFreight,
      optimalExcludedRowKeys,
      quoteDeliveredOfferByRowKey,
    ]);

    const optimalCompetitorRowKey = useMemo(() => {
      let bestRowKey: string | null = null;
      let bestDeliveredOffer = Number.POSITIVE_INFINITY;

      quotesWithFreight.forEach((quote) => {
        if (!quote.competitor) {
          return;
        }
        const rowKey = getQuoteRowKey(quote);
        if (optimalExcludedRowKeys.has(rowKey)) {
          return;
        }
        const deliveredOffer = quoteDeliveredOfferByRowKey.get(rowKey);
        if (deliveredOffer === undefined || !Number.isFinite(deliveredOffer)) {
          return;
        }
        if (deliveredOffer < bestDeliveredOffer) {
          bestDeliveredOffer = deliveredOffer;
          bestRowKey = rowKey;
        }
      });

      return bestRowKey;
    }, [
      quotesWithFreight,
      optimalExcludedRowKeys,
      quoteDeliveredOfferByRowKey,
    ]);

    const numberOfCarriersToShow = 1;
    const destinationCarrierIds = toCarrierIds(
      destination.details?.carriers || [],
    );
    // if destination is a city then truck is defaulted on. if not then check the destinations enabled inbound modes
    const hasTruckInbound =
      destination.isCity || destination.details?.inboundModes.includes("TRUCK");
    const hasRailInbound = destination.details?.inboundModes.includes("RAIL");

    useEffect(() => {
      if (resetTrigger !== undefined && resetTrigger > 0) {
        setGlobalDeliveredOfferInput("");
      }
    }, [resetTrigger]);

    // if another panel triggers the truck (with no freight) to expand then open it
    // if the trigger is 0 or less (because another button click has reset it) close it
    useEffect(() => {
      if (expandTruckOriginsWithNoFreightTrigger > 0) {
        setShowTruckQuotesWithNoFreight(true);
      } else {
        setShowTruckQuotesWithNoFreight(false);
      }
    }, [expandTruckOriginsWithNoFreightTrigger]);

    // if another panel triggers the rail (with no freight) to expand then open it
    // if the trigger is 0 or less (because another button click has reset it) close it
    useEffect(() => {
      if (expandRailOriginsWithNoFreightTrigger > 0) {
        setShowRailQuotesWithNoFreight(true);
      } else {
        setShowRailQuotesWithNoFreight(false);
      }
    }, [expandRailOriginsWithNoFreightTrigger]);

    useEffect(() => {
      if (!truckQuotesWithNoFreight.length || !currentFilters?.month) return;
      const load = async () => {
        setTruckMissingLoading(true);
        try {
          const sections = await buildMissingTruckOriginSections(
            truckQuotesWithNoFreight,
            destination,
            { product, month: currentFilters.month },
          );
          setTruckMissingRows(buildRows(sections));
        } catch {
          setTruckMissingRows([]);
        } finally {
          setTruckMissingLoading(false);
        }
      };
      load();
    }, [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      truckQuotesWithNoFreight.length,
      destination.id,
      destination.isCity,
      product,
      currentFilters?.month,
    ]);

    useEffect(() => {
      if (
        !manageRailRate ||
        !railQuotesWithNoFreight.length ||
        !currentFilters?.month
      )
        return;
      const load = async () => {
        setRailMissingLoading(true);
        try {
          const sections = await buildMissingRailOriginSections(
            railQuotesWithNoFreight,
            destination,
            { product, month: currentFilters.month },
          );
          setRailMissingRows(buildRailRows(sections, destination.id));
        } catch {
          setRailMissingRows([]);
        } finally {
          setRailMissingLoading(false);
        }
      };
      load();
    }, [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      manageRailRate,
      railQuotesWithNoFreight.length,
      destination.id,
      destination.isCity,
      product,
      currentFilters?.month,
    ]);

    const handleDlvdOfferChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setGlobalDeliveredOfferInput(event.target.value);
      setGlobalOfferInputChanged(event.target.value.trim() !== "");
      setOriginalValues({});
      setRowDeliveredValues({});
      setRowNetbackValues({});
    };

    const handleResetClick = () => {
      setGlobalDeliveredOfferInput("");
      setGlobalOfferInputChanged(false);
      setOriginalValues({});
      setRowDeliveredValues({});
      setRowNetbackValues({});
    };

    const handleRowDeliveredOfferClick = (
      rowKey: string,
      currentDeliveredOffer: number,
      originalDeliveredOffer: number,
      originalNetback: number,
    ) => {
      setEditingDeliveredRowKey(rowKey);
      setRowDeliveredValues((prev) => ({
        ...prev,
        [rowKey]: String(currentDeliveredOffer),
      }));

      if (!originalValues[rowKey]) {
        setOriginalValues((prev) => ({
          ...prev,
          [rowKey]: {
            dlvd: String(originalDeliveredOffer),
            fob: String(originalNetback),
          },
        }));
      }
    };
    const handleRowNetbackClick = (
      rowKey: string,
      currentNetback: number,
      originalDeliveredOffer: number,
      originalNetback: number,
    ) => {
      replaceNetbackOnNextDigit.current = rowKey;
      setEditingNetbackRowKey(rowKey);
      setRowNetbackValues((prev) => ({
        ...prev,
        [rowKey]: String(currentNetback),
      }));

      if (!originalValues[rowKey]) {
        setOriginalValues((prev) => ({
          ...prev,
          [rowKey]: {
            dlvd: String(originalDeliveredOffer),
            fob: String(originalNetback),
          },
        }));
      }
    };

    const handleRowDeliveredChange = (
      rowKey: string,
      deliveredOffer: string,
      freight: number,
    ) => {
      setRowDeliveredValues((prev) => ({ ...prev, [rowKey]: deliveredOffer }));

      const dlvdValue = parseFloat(deliveredOffer);
      if (!Number.isNaN(dlvdValue)) {
        const newFob = (dlvdValue - freight).toFixed(2);
        setRowNetbackValues((prev) => ({ ...prev, [rowKey]: newFob }));
      }
    };
    const handleRowNetbackChange = (
      rowKey: string,
      netback: string,
      freight: number,
    ) => {
      setRowNetbackValues((prev) => ({ ...prev, [rowKey]: netback }));

      const fobValue = parseFloat(netback);
      if (!Number.isNaN(fobValue)) {
        const newDlvd = (fobValue + freight).toFixed(2);
        setRowDeliveredValues((prev) => ({ ...prev, [rowKey]: newDlvd }));
      }
    };

    const handleRowDeliveredBlur = () => {
      setEditingDeliveredRowKey(null);
    };
    const handleRowNetbackBlur = () => {
      replaceNetbackOnNextDigit.current = null;
      setEditingNetbackRowKey(null);
    };

    const handleRowDeliveredKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        setEditingDeliveredRowKey(null);
      }
    };

    const handleRowNetbackKeyDown = (
      e: React.KeyboardEvent,
      rowKey: string,
      freight: number,
    ) => {
      if (
        replaceNetbackOnNextDigit.current === rowKey &&
        /^[0-9]$/.test(e.key) &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        replaceNetbackOnNextDigit.current = null;
        handleRowNetbackChange(rowKey, e.key, freight);
        return;
      }
      if (e.key === "Enter") {
        setEditingNetbackRowKey(null);
      }
    };

    const handleResetRowValues = (rowKey: string) => {
      const original = originalValues[rowKey];
      if (original) {
        setRowDeliveredValues((prev) => {
          const newValues = { ...prev };
          delete newValues[rowKey];
          return newValues;
        });
        setRowNetbackValues((prev) => {
          const newValues = { ...prev };
          delete newValues[rowKey];
          return newValues;
        });
        setOriginalValues((prev) => {
          const newValues = { ...prev };
          delete newValues[rowKey];
          return newValues;
        });
      }
    };

    // TODO: why is hastings equalizer row MASSIVE compared to the others?
    // TODO: icons on fav origins are grey
    // TODO: rail missing row needs more padding if truck row is missing
    return (
      <Box
        id="all-origin-table"
        className={styles.originTableContainer}
        ref={ref}
        data-origin-table-root="true"
      >
        {/* Header Section */}
        {(hasTruckInbound || hasRailInbound) && (
          <Box
            className={styles.originTableHeaderBox}
            style={{ gap: globalDeliveredOfferInput ? "45px" : "65px" }}
          >
            {/* Title */}
            <span className={styles.allOriginsTitle}>
              <img
                src={icons.MultiLocationsIcon}
                alt="MultiLocations Icon"
                style={{ height: "20px", width: "20px" }}
              />
              <span className={styles.originTableHeader}>All Origins</span>
            </span>

            {/* Global Delivered Offer Input */}
            <Box className={styles.originDlvdOfferBox}>
              <div className={styles.dlvdOfferLabel}>DLVD Offer</div>
              <TextField
                id="nb-input"
                variant="outlined"
                className={styles.dlvdOfferInput}
                value={globalDeliveredOfferInput}
                onChange={handleDlvdOfferChange}
                onFocus={(event) => event.target.select()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        "& .MuiTypography-root": {
                          fontFamily: "Inter",
                          fontWeight: 400,
                          fontSize: "12px",
                          lineHeight: "130%",
                          letterSpacing: "0%",
                          color: "#334155",
                        },
                      }}
                    >
                      $
                    </InputAdornment>
                  ),
                  sx: {
                    "& input": {
                      paddingLeft: "0px",
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "28px",
                    borderRadius: "6px",
                    backgroundColor: "#FEFEFE",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "1px solid #E2E8F0",
                    },
                  },
                  width: "90px",
                }}
              />
              {globalDeliveredOfferInput &&
                globalDeliveredOfferInput.trim() !== "" && (
                  <Tooltip
                    title="reset to FOB price"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          background: "#ddeefc",
                          color: "#1e3a8a",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#ddeefc",
                        },
                      },
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      className={styles.dlvdRefreshButton}
                      id="dlvd-offer-refresh-button"
                      onClick={handleResetClick}
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        background:
                          "linear-gradient(0deg, #FFF 0%, #FFF 100%), #FFF",
                        padding: "6px",
                        width: "30px",
                        minWidth: "30px",
                        height: "28px",
                      }}
                    >
                      <img
                        src={icons.RefreshButtonIcon}
                        alt="MultiLocations Refresh Icon"
                        className={`${styles.IconIncrease20} ${styles.shadowBackground}`}
                      />
                    </Button>
                  </Tooltip>
                )}
            </Box>

            {/* Inbound Modes Summary */}
            <div className={styles.inboundModesContainer}>
              <span className={styles.inboundModesText}>Inbound Modes</span>
              {!destination.isCity && (
                <Button
                  size="small"
                  onClick={(event) => {
                    setDisplayInboundModeTogglePanel(true);
                    setAnchorForInboundModesTogglePanel(event.currentTarget);
                    setInboundModes(
                      toModes(
                        destination.details?.inboundModes || [],
                        destination.details?.outboundModes || [],
                      ),
                    );
                    setDestinationToTroubleshoot(destination);
                  }}
                  sx={{
                    color: "#1d4ed8",
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 400,
                    fontStyle: "normal",
                    padding: "0",
                  }}
                >
                  Add/Remove
                </Button>
              )}
              {/* Only show truck icon if inbound truck mode is available or if the destination is a city */}
              {(destination.isCity ||
                destination.details?.inboundModes.includes("TRUCK")) && (
                <TruckIconFO color="#60A5FA" />
              )}

              {/* Only show rail icon if inbound rail mode is available.
                Only show 2 carriers for Rail. if more than 2 carriers show the rest as a runoff like +{number of carriers not shown} */}
              {destination.details?.inboundModes.includes("RAIL") && (
                <>
                  {destination.details?.inboundModes.includes("TRUCK") && (
                    <span style={{ fontSize: "12px", color: "#DBE1E8" }}>
                      |
                    </span>
                  )}
                  <span className={styles.inboundModesRail}>
                    <RailIconFO color="#F97316" />
                    {destinationCarrierIds.map((carrier, index, carriers) =>
                      index > numberOfCarriersToShow ? null : (
                        <>
                          <span
                            key={carrier}
                            className={
                              index === numberOfCarriersToShow
                                ? styles.inboundModesRunoffCarriers
                                : styles.inboundModesCarrier
                            }
                            onMouseEnter={(event) => {
                              if (index === numberOfCarriersToShow) {
                                setDisplayAdditionalCarriersPanel(true);
                                setAdditionalCarriers(destinationCarrierIds);
                                setAnchorForAdditionalCarriersPanel(
                                  event.currentTarget,
                                );
                              }
                            }}
                            onMouseLeave={() => {
                              setDisplayAdditionalCarriersPanel(false);
                              setAdditionalCarriers([]);
                              setAnchorForAdditionalCarriersPanel(null);
                            }}
                          >
                            {index === numberOfCarriersToShow
                              ? `+${carriers.length - numberOfCarriersToShow}`
                              : carrier}
                          </span>
                          {/* {index < numberOfCarriersToShow &&
                              index !== carriers.length - 1 && (
                                <span className={styles.inboundModesDot} />
                              )} */}
                        </>
                      ),
                    )}
                  </span>
                </>
              )}
            </div>
          </Box>
        )}

        {/* Origin Quotes Table */}
        {(hasTruckInbound || hasRailInbound) &&
          quotesWithFreight.length > 0 && (
            // <Box sx={ { borderRadius: "12px", overflow: "visible", boxShadow: "1px 2px 2px 0px #0000000d"} }>
            <Paper
              ref={paperRef}
              className={styles.originTableContainerPaper}
              variant="outlined"
              sx={{
                borderRadius: isHeaderSticky ? "0 0 12px 12px" : "12px",
                overflow: "clip",
              }}
            >
              <TableContainer sx={{ overflow: "visible" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    "& .MuiTableRow-root:last-child td, & .MuiTableRow-root:last-child th":
                      { borderBottom: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.originHeaderCell}>
                        Origin
                      </TableCell>
                      <TableCell className={styles.originHeaderCell}>
                        Mode
                      </TableCell>
                      <TableCell className={styles.originHeaderCell}>
                        <span style={{ whiteSpace: "nowrap" }}>DLVD Offer</span>
                      </TableCell>
                      <TableCell
                        className={styles.originHeaderCell}
                        style={{ paddingLeft: "7px" }}
                      >
                        Freight
                      </TableCell>
                      <TableCell className={styles.originHeaderCell}>
                        <span style={{ whiteSpace: "nowrap" }}>
                          FOB Netback
                        </span>
                      </TableCell>
                      <TableCell className={styles.originHeaderCell}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortQuotesByNetbackAndDlvd(
                      quotesWithFreight,
                      kochPricePreference,
                    ).map((quote) => {
                      // dot icon color
                      const isKoch = !quote.competitor;
                      const dotIcon = isKoch
                        ? icons.BlueDotIcon
                        : icons.RedDotIcon;
                      const originNameClass = isKoch
                        ? styles.originNameBlue
                        : styles.originNameRed;

                      // determine if this quote is the cheapest for Koch or Competitor
                      // generate unique row key (rail rows use minCars to differentiate. no minCars means mode of TRUCK)
                      const rowKey = getQuoteRowKey(quote);

                      // determine if this quote is the lowest DLVD offer row for Koch or Competitor
                      const isCheapestKochQuote = rowKey === optimalKochRowKey;
                      const isCheapestCompetitorQuote =
                        rowKey === optimalCompetitorRowKey;

                      // determine initial display values from quote
                      const {
                        initialDeliveredOffer,
                        freight,
                        initialNetback,
                        initialNetbackDisplay,
                        netbackSubText,
                      } = getOriginRowPricing(quote, kochPricePreference);
                      const { isQuoted, isNotified } =
                        isRailFreightQuotedOrNotified(
                          railTeamNotifiedRows,
                          quote,
                        );

                      // determine if this row is being hovered or edited
                      const isHoveredDeliveredRow =
                        hoveredDeliveredRowKey === rowKey;
                      const isHoveredNetbackRow =
                        hoveredNetbackRowKey === rowKey;
                      const isEditingThisRow =
                        editingDeliveredRowKey === rowKey;
                      const isEditingFobRow = editingNetbackRowKey === rowKey;

                      // get row specific input values
                      const rowSpecificDeliveredOffer =
                        rowDeliveredValues[rowKey];
                      const rowSpecificNetback = rowNetbackValues[rowKey];

                      // determine if values have been modified from original
                      const isDeliveredOfferModified =
                        (rowSpecificDeliveredOffer &&
                          originalValues[rowKey] &&
                          rowSpecificDeliveredOffer !==
                            originalValues[rowKey].dlvd) ||
                        globalOfferInputChanged;
                      const isNetbackModified =
                        (rowSpecificNetback &&
                          originalValues[rowKey] &&
                          rowSpecificNetback !== originalValues[rowKey].fob) ||
                        globalOfferInputChanged;

                      // determine final display value for delivered price based on user inputs
                      let finalDeliveredOffer: number;
                      if (rowSpecificDeliveredOffer) {
                        // use delivered offer from input on the individual row
                        finalDeliveredOffer = Number(rowSpecificDeliveredOffer);
                      } else if (globalDeliveredOfferInput.trim() !== "") {
                        // use global delivered offer input
                        finalDeliveredOffer = Number(globalDeliveredOfferInput);
                      } else {
                        // use initial delivered offer from quote data
                        finalDeliveredOffer = initialDeliveredOffer;
                      }

                      // determine final FOB netback value/text from user inputs (falls back to quote data when no override is set)
                      const netbackOverride = computeNetbackOverride(
                        freight,
                        rowSpecificNetback,
                        rowSpecificDeliveredOffer,
                        globalDeliveredOfferInput,
                      );
                      const finalNetbackValue =
                        netbackOverride !== null
                          ? Number(netbackOverride.toFixed(0))
                          : initialNetback;
                      const displayNetbackText =
                        netbackOverride !== null
                          ? formatNetback(netbackOverride)
                          : initialNetbackDisplay;

                      // determine styles for netback value
                      const {
                        backgroundColor,
                        netbackTextColor,
                        deltaColor,
                        netbackDelta,
                      } = calculateFobDifference(
                        finalNetbackValue,
                        initialNetback,
                        initialNetback,
                        !!isNetbackModified,
                        parseFobSubTextRange(netbackSubText),
                      );
                      const isRowModified =
                        isDeliveredOfferModified || isNetbackModified;
                      return (
                        <TableRow
                          key={rowKey}
                          sx={{
                            "& > td": {
                              padding: "10px",
                              background: isCheapestKochQuote
                                ? "rgba(239, 246, 255, 0.5)"
                                : isCheapestCompetitorQuote
                                ? "rgba(100, 116, 139, 0.05)"
                                : "transparent",
                              borderBottom: isCheapestKochQuote
                                ? "1px solid #bfdbfe"
                                : isCheapestCompetitorQuote
                                ? "1px solid rgba(100, 116, 139, 0.15)"
                                : "1px solid #dfe3e8",
                              borderTop: isCheapestKochQuote
                                ? "1px solid #bfdbfe"
                                : isCheapestCompetitorQuote
                                ? "1px solid rgba(100, 116, 139, 0.15)"
                                : "1px solid #dfe3e8",
                            },
                          }}
                        >
                          {/* Origin Name Cell */}
                          <TableCell>
                            <Box className={styles.originNameCellBox}>
                              <img
                                data-tooltip-id="dot-icon"
                                src={dotIcon}
                                alt="Dot Icon"
                                className={styles.dotIcon}
                              />
                              <span
                                id={`origin-table-origin-name-${rowKey}`}
                                className={`${styles.originName} ${originNameClass}`}
                              >
                                {quote.origin.name}
                              </span>
                            </Box>
                          </TableCell>

                          {/* Mode Cell */}
                          <TableCell>
                            <Box className={styles.modeCell}>
                              {quote.mode.toUpperCase() === "RAIL" ? (
                                <RailIconFO color="#F97316" />
                              ) : (
                                <TruckIconFO color="#60A5FA" />
                              )}
                              <span className={styles.modeText}>
                                {quote.mode.toUpperCase() === "TRUCK" && (
                                  <>TRUCK</>
                                )}
                                {quote.mode.toUpperCase() === "RAIL" &&
                                  (quote.freight &&
                                  "minCars" in quote.freight &&
                                  quote.freight.minCars > 1 ? (
                                    <> ({quote.freight.minCars}+ UT)</>
                                  ) : (
                                    "SINGLE"
                                  ))}
                              </span>
                            </Box>
                          </TableCell>

                          {/* DLVD Offer Cell */}
                          <TableCell
                            id="origin-table-dlvd-offer"
                            className={styles.priceBold}
                            onMouseEnter={() =>
                              setHoveredDeliveredRowKey(rowKey)
                            }
                            onMouseLeave={() => setHoveredDeliveredRowKey(null)}
                            sx={{
                              cursor: "pointer",
                              color: isHoveredDeliveredRow
                                ? "#2563EB"
                                : "inherit",
                              transition: "color 0.2s ease",
                              position: "relative",
                            }}
                          >
                            {isEditingThisRow ? (
                              <TextField
                                autoFocus
                                value={rowSpecificDeliveredOffer || ""}
                                onFocus={(event) => event.target.select()}
                                onChange={(e) =>
                                  handleRowDeliveredChange(
                                    rowKey,
                                    e.target.value,
                                    freight,
                                  )
                                }
                                onBlur={handleRowDeliveredBlur}
                                onKeyDown={handleRowDeliveredKeyDown}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px",
                                    fontSize: "14px",
                                  },
                                  width: "80px",
                                }}
                              />
                            ) : (
                              <Box
                                onClick={() =>
                                  handleRowDeliveredOfferClick(
                                    rowKey,
                                    finalDeliveredOffer,
                                    initialDeliveredOffer,
                                    initialNetback,
                                  )
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {!freight
                                  ? "--"
                                  : `$${finalDeliveredOffer.toFixed(0)}`}

                                {isHoveredDeliveredRow &&
                                  !isDeliveredOfferModified && (
                                    <img
                                      src={icons.EditPencilBlueIcon}
                                      alt="Edit Icon"
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                      }}
                                    />
                                  )}
                                {isDeliveredOfferModified &&
                                  !globalOfferInputChanged && (
                                    <Box
                                      component="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetRowValues(rowKey);
                                      }}
                                      sx={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <img
                                        src={icons.RefreshButtonIcon}
                                        alt="Reset Icon"
                                        className={`${styles.IconIncrease20} ${styles.shadowBackground}`}
                                      />
                                    </Box>
                                  )}
                              </Box>
                            )}
                          </TableCell>

                          {/* Freight Cell */}
                          <TableCell style={{ paddingLeft: "1px" }}>
                            <Button
                              className={styles.freightCell}
                              sx={{
                                borderRadius: "3px",
                                color: isNotified
                                  ? "#009F5C"
                                  : isQuoted
                                  ? "#1d4ed8"
                                  : "#475569",
                                backgroundColor: "transparent",
                                "&:hover": {
                                  color: isNotified
                                    ? "#007543"
                                    : isQuoted
                                    ? "#1e40af"
                                    : "#2563EB",
                                  backgroundColor: "#E8EFF7",
                                },
                              }}
                              onClick={(event) => {
                                if (quote.mode === "RAIL") {
                                  setRailFreightDetailContext({
                                    quote,
                                    destination,
                                    rowKey,
                                  });
                                  setDisplayFreightDetailsPanel(true);
                                }
                                if (quote.mode === "TRUCK") {
                                  setDisplayTruckFreightDetailsPanel(true);
                                  setSelectedTruckDetailRecord(
                                    quote.freight as FreightTruck,
                                  );
                                  setAnchorForTruckDetailsPanel(
                                    event.currentTarget,
                                  );
                                }
                              }}
                            >
                              <span className={styles.destinationFreight}>
                                ${freight}
                              </span>
                              {isQuoted && !isNotified && <QuotedRateIcon />}
                              {isNotified && <QuotedRateNotifiedIcon />}
                            </Button>
                          </TableCell>

                          {/* FOB Netback Cell */}
                          <TableCell
                            onMouseEnter={() => setHoveredNetbackRowKey(rowKey)}
                            onMouseLeave={() => setHoveredNetbackRowKey(null)}
                            sx={{
                              cursor: "pointer",
                              transition: "color 0.2s ease",
                            }}
                          >
                            {isEditingFobRow ? (
                              <TextField
                                autoFocus
                                value={rowSpecificNetback || ""}
                                onFocus={(event) => event.target.select()}
                                onChange={(e) => {
                                  replaceNetbackOnNextDigit.current = null;
                                  handleRowNetbackChange(
                                    rowKey,
                                    e.target.value,
                                    freight,
                                  );
                                }}
                                onBlur={handleRowNetbackBlur}
                                onKeyDown={(event) =>
                                  handleRowNetbackKeyDown(event, rowKey, freight)
                                }
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "28px",
                                    fontSize: "14px",
                                  },
                                  width: "80px",
                                }}
                              />
                            ) : (
                              <Box
                                onClick={() =>
                                  handleRowNetbackClick(
                                    rowKey,
                                    finalNetbackValue,
                                    initialDeliveredOffer,
                                    initialNetback,
                                  )
                                }
                                sx={{ paddingTop: "6px" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    className={styles.priceBold}
                                    sx={{
                                      color: isHoveredNetbackRow
                                        ? "#2563EB"
                                        : netbackTextColor,
                                      display: "flex",
                                      alignItems: "center",
                                      backgroundColor,
                                      height: isRowModified ? "10px" : "0px",
                                      width: globalOfferInputChanged
                                        ? "44%"
                                        : "40%",
                                      borderRadius: "5px",
                                      paddingTop: "3px",
                                      paddingBottom: "8px",
                                      paddingLeft: isRowModified ? "1px" : "0",
                                      paddingRight: isRowModified ? "1px" : "0",
                                      marginRight: isRowModified ? "4px" : "0",
                                    }}
                                  >
                                    <span
                                      id="final-netback-price"
                                      style={{
                                        paddingTop: isRowModified ? "3px" : "0",
                                      }}
                                    >
                                      {displayNetbackText}
                                    </span>
                                    {isHoveredNetbackRow && !isRowModified && (
                                      <img
                                        src={icons.EditPencilBlueIcon}
                                        alt="Edit Pencil Icon"
                                        className={`${styles.IconIncrease20}`}
                                        style={{ marginLeft: "4px" }}
                                      />
                                    )}
                                  </Box>
                                  {isRowModified && (
                                    <>
                                      {netbackDelta !== "0" && (
                                        <span
                                          id={`netback-delta-${rowKey}`}
                                          style={{
                                            color: deltaColor,
                                            fontSize: "12px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {netbackDelta}
                                        </span>
                                      )}
                                      {!globalOfferInputChanged && (
                                        <Box
                                          component="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetRowValues(rowKey);
                                          }}
                                          sx={{
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            marginLeft: "4px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <img
                                            src={icons.RefreshButtonIcon}
                                            alt="Reset Icon"
                                            className={`${styles.IconIncrease20} ${styles.shadowBackground}`}
                                          />
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div
                                  id="fobSubTextRange"
                                  className={styles.fobSubText}
                                >
                                  {netbackSubText}
                                </div>
                              </Box>
                            )}
                          </TableCell>
                          {/* Actions Cell */}
                          <TableCell>
                            {isCheapestKochQuote && (
                              <span className={styles.optimalText}>
                                Optimal Koch Origin
                              </span>
                            )}
                            {isCheapestCompetitorQuote && (
                              <span className={styles.optimalText}>
                                Optimal Competitor Origin
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>{" "}
            </Paper>
          )}

        {/* TRUCK quotes with no freight */}
        {/* if inbound truck is turned off then the drop down table becomes a quick add button */}
        {(!hasTruckInbound || truckQuotesWithNoFreight.length > 0) && (
          <div
            className={styles.quotesWithNoFreightContainer}
            style={{ paddingTop: "30px" }}
          >
            <TableContainer style={{ overflow: "visible" }}>
              <Table
                sx={{ borderCollapse: "separate", borderSpacing: "revert" }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderRadius: "10.5px",
                        border: "1px solid #BEDBFF",
                        background: "#F1F5F9",
                        boxShadow: "0 1px 4px -1px rgba(0, 0, 0, 0.05)",
                        width: "100%",
                        paddingRight: 0,
                        overflow: "visible",
                      }}
                    >
                      {/* Header Section */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingTop: "12px",
                          paddingBottom: "12px",
                          paddingLeft: "8px",
                          paddingRight: "16px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              paddingLeft: "8px",
                              paddingTop: "6px",
                              flexShrink: 0,
                            }}
                          >
                            {!hasTruckInbound ? (
                              <TruckIconFO color="#60A5FA" />
                            ) : (
                              <WarningTriangleIcon
                                stroke="#2FA8DE"
                                strokeWidth={1.7}
                              />
                            )}
                          </span>
                          <span
                            className={styles.quotesWithNoFreightText}
                            style={{
                              fontSize: "16px",
                              lineHeight: "21px",
                              paddingLeft: 0,
                            }}
                          >
                            {!hasTruckInbound
                              ? "Can This Location Support More Inbound Modes?"
                              : "Missing Truck Origins"}
                          </span>
                        </Box>

                        {!hasTruckInbound ? (
                          <Button
                            size="small"
                            onClick={async () => {
                              await updateInboundModes(
                                destination,
                                true,
                                "TRUCK",
                              );
                              // upon successful update close panels, refetch data, and expand destination
                              setMultiLocationDestinations([]);
                              triggerRefetchMultiLocation();
                              setExpandedDestination(
                                {
                                  id: destination.id,
                                  name: destination.name,
                                },
                                {
                                  expandTruckWithNoFreight: true,
                                  expandSurroundingLocations:
                                    destination.isSurroundingLocation,
                                },
                              );
                            }}
                            sx={{
                              color: "#1d4ed8",
                              fontFamily: "Inter",
                              fontSize: "14px",
                              fontWeight: 500,
                              fontStyle: "normal",
                              "&:hover": { borderRadius: "8px" },
                            }}
                          >
                            + Add Inbound Truck
                          </Button>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              gap: "15px",
                              alignItems: "center",
                            }}
                          >
                            {hasTruckInbound && (
                              <span
                                className={styles.quotesWithNoTruckFreightCount}
                              >
                                {truckQuotesWithNoFreight.length}{" "}
                                {truckQuotesWithNoFreight.length > 1
                                  ? "locations"
                                  : "location"}
                              </span>
                            )}
                            <IconButton
                              size="small"
                              disableRipple
                              data-testid="truck-no-freight-toggle"
                              onClick={() => {
                                if (showTruckQuotesWithNoFreight) {
                                  resetExpandTruckOriginsWithNoFreight();
                                  return;
                                }
                                setExpandTruckOriginsWithNoFreightTrigger();
                              }}
                              sx={{ paddingLeft: "6px" }}
                            >
                              {showTruckQuotesWithNoFreight ? (
                                <Box
                                  sx={{
                                    backgroundColor: "#DBEAFE",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 24.5,
                                    width: 24.5,
                                  }}
                                >
                                  <KeyboardArrowUp sx={{ color: "#1D4ED8" }} />
                                </Box>
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </div>
                        )}
                      </Box>

                      {/* Collapse Section */}
                      <Collapse
                        in={showTruckQuotesWithNoFreight}
                        timeout="auto"
                      >
                        <Box sx={{ px: "16px", pb: "16px" }}>
                          <TruckMissingOriginsTable
                            rows={truckMissingRows}
                            loading={truckMissingLoading}
                            destination={destination}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        {/* RAIL quotes with no freight */}
        {/* if inbound rail is turned off then the drop down table becomes a quick add button */}
        {/* show if destination does not have rail turned on, rail is turned on but there are records with no freight and never show for city pins */}

        {manageRailRate &&
          (!hasRailInbound || railQuotesWithNoFreight.length > 0) &&
          !destination.isCity && (
            <div
              className={styles.quotesWithNoFreightContainer}
              style={{ paddingTop: "16px" }}
            >
              <TableContainer style={{ overflow: "visible" }}>
                <Table
                  sx={{ borderCollapse: "separate", borderSpacing: "revert" }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          borderRadius: "10.5px",
                          border: "1px solid #FFD6A8",
                          background: "#FAF9F8",
                          boxShadow: "0 1px 4px -1px rgba(0, 0, 0, 0.05)",
                          width: "100%",
                          paddingRight: 0,
                          overflow: "visible",
                        }}
                      >
                        {/* Header Section */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                            paddingLeft: "8px",
                            paddingRight: "16px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <span
                              style={{
                                paddingLeft: "8px",
                                paddingTop: "6px",
                                flexShrink: 0,
                              }}
                            >
                              {!hasRailInbound ? (
                                <RailIconFO color="#F97316" />
                              ) : (
                                <WarningTriangleIcon
                                  stroke="#E17100"
                                  strokeWidth={1.7}
                                />
                              )}
                            </span>
                            <span
                              className={styles.quotesWithNoFreightText}
                              style={{
                                fontSize: "16px",
                                lineHeight: "21px",
                                paddingLeft: 0,
                              }}
                            >
                              {!hasRailInbound
                                ? "Can This Location Support More Inbound Modes?"
                                : "Missing Rail Origins"}
                            </span>
                          </Box>
                          {!hasRailInbound ? (
                            <Button
                              id="add-inbound-rail-button"
                              size="small"
                              onClick={() => {
                                setDestinationToTroubleshoot(destination);
                                setDisplayAddInboundRailPanel(true);
                              }}
                              sx={{
                                color: "#1d4ed8",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontStyle: "normal",
                                "&:hover": { borderRadius: "8px" },
                              }}
                            >
                              + Add Inbound Rail
                            </Button>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                gap: "15px",
                                alignItems: "center",
                              }}
                            >
                              {hasRailInbound && (
                                <span
                                  id={`rail-missing-origins-count-${destination.id}`}
                                  className={
                                    styles.quotesWithNoRailFreightCount
                                  }
                                >
                                  {railQuotesWithNoFreight.length}{" "}
                                  {railQuotesWithNoFreight.length > 1
                                    ? "locations"
                                    : "location"}
                                </span>
                              )}
                              <IconButton
                                id="rail-no-freight-toggle"
                                size="small"
                                disableRipple
                                onClick={() => {
                                  if (showRailQuotesWithNoFreight) {
                                    resetExpandRailOriginsWithNoFreight();
                                    return;
                                  }
                                  setExpandRailOriginsWithNoFreightTrigger();
                                }}
                              >
                                {showRailQuotesWithNoFreight ? (
                                  <Box
                                    sx={{
                                      backgroundColor: "#DBEAFE",
                                      borderRadius: "6px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      height: 24.5,
                                      width: 24.5,
                                      overflow: "visible",
                                    }}
                                  >
                                    <KeyboardArrowUp
                                      sx={{ color: "#1D4ED8" }}
                                    />
                                  </Box>
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </div>
                          )}
                        </Box>

                        {/* Collapse Section */}
                        <Collapse
                          in={showRailQuotesWithNoFreight}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ px: "16px", pb: "16px" }}>
                            <RailMissingOriginsTable
                              rows={railMissingRows}
                              loading={railMissingLoading}
                              destination={destination}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        {/* OLD VERSION */}
        {/* RAIL quotes with no freight */}
        {!manageRailRate &&
          (!hasRailInbound || railQuotesWithNoFreight.length > 0) &&
          !destination.isCity && (
            <div
              className={styles.quotesWithNoFreightContainer}
              style={{ paddingTop: "16px" }}
            >
              <TableContainer>
                <Table
                  sx={{ borderCollapse: "separate", borderSpacing: "revert" }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          borderRadius: "10.5px",
                          border: "1px solid #FFD6A8",
                          background: "#FAF9F8",
                          boxShadow: "0 1px 4px -1px rgba(0, 0, 0, 0.05)",
                          width: "100%",
                          paddingRight: 0,
                          overflow: "hidden",
                        }}
                      >
                        {/* Header Section */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                            paddingLeft: "8px",
                            paddingRight: "16px",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <span
                              style={{ paddingLeft: "8px", paddingTop: "6px" }}
                            >
                              {!hasRailInbound ? (
                                <RailIconFO color="#F97316" />
                              ) : (
                                <WarningTriangleIcon
                                  stroke="#E17100"
                                  strokeWidth={1.7}
                                />
                              )}
                            </span>
                            <span className={styles.quotesWithNoFreightText}>
                              {!hasRailInbound
                                ? "Can This Location Support More Inbound Modes?"
                                : "Rail Origins Needing Setup or Missing Freight Guidance"}
                            </span>
                          </Box>
                          {!hasRailInbound ? (
                            <Button
                              id="add-inbound-rail-button-old"
                              size="small"
                              onClick={() => {
                                setDestinationToTroubleshoot(destination);
                                setDisplayAddInboundRailPanel(true);
                              }}
                              sx={{
                                color: "#1d4ed8",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontStyle: "normal",
                                "&:hover": { borderRadius: "8px" },
                              }}
                            >
                              + Add Inbound Rail
                            </Button>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                gap: "15px",
                                alignItems: "center",
                              }}
                            >
                              {hasRailInbound && (
                                <span
                                  className={
                                    styles.quotesWithNoRailFreightCount
                                  }
                                >
                                  {railQuotesWithNoFreight.length}{" "}
                                  {railQuotesWithNoFreight.length > 1
                                    ? "locations"
                                    : "location"}
                                </span>
                              )}
                              <IconButton
                                size="small"
                                disableRipple
                                onClick={() => {
                                  if (showRailQuotesWithNoFreight) {
                                    resetExpandRailOriginsWithNoFreight();
                                    return;
                                  }
                                  setExpandRailOriginsWithNoFreightTrigger();
                                }}
                              >
                                {showRailQuotesWithNoFreight ? (
                                  <Box
                                    sx={{
                                      backgroundColor: "#DBEAFE",
                                      borderRadius: "6px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      height: 24.5,
                                      width: 24.5,
                                    }}
                                  >
                                    <KeyboardArrowUp
                                      sx={{ color: "#1D4ED8" }}
                                    />
                                  </Box>
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </div>
                          )}
                        </Box>

                        {/* Collapse Section */}
                        <Collapse
                          in={showRailQuotesWithNoFreight}
                          timeout="auto"
                          unmountOnExit
                        >
                          <MissingFreightTable
                            destination={destination}
                            quotes={railQuotesWithNoFreight}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
      </Box>
    );
  },
);
